import { openaiCache } from '../cache-service';
import { readAdminCredentialRevision } from '../storage/admin-credential-revision';
import { getCachedOpenAiCosts } from './cost-cache';
import { withProviderResourceLock, LIVE_RESOURCE_TTL_MS } from '../provider-resource-cache';
import { mergeTimeRanges, subtractTimeRanges, splitTimeRangesByMaxSpan } from '../range-coverage';
import { DAY_SECONDS, finalizationBoundary, RETAINED_FRESH_UNTIL } from './history-policy';
export { mergeTimeRanges, subtractTimeRanges, splitTimeRangesByMaxSpan } from '../range-coverage';
import type { OpenAiUsageBucket, OpenAiUsagePage } from './types';
import type { ApiResponse, OpenAiSegmentFetchResult, OpenAiUsageBucketWidth, OpenAiUsageSegmentPayload, TimeRange } from './types';
import {
  getCompletionsUsage,
  getEmbeddingsUsage,
  getModeratioUsage,
  getImagesUsage,
  getAudioSpeechesUsage,
  getAudioTranscriptionsUsage
} from './usage-endpoints';

export const OPENAI_USAGE_SEGMENT_CACHE_VERSION = '2026-09-12-segment-v5'; // complete pagination + retained finalized buckets
export const OPENAI_SEGMENT_CACHE_TTL_RECENT_MS = LIVE_RESOURCE_TTL_MS;
export const OPENAI_SEGMENT_CACHE_TTL_HISTORICAL_MS = null;
const COST_RANGE_NOW_TOLERANCE_SECONDS = 5 * 60;

export const resolveOpenAiSegmentCacheTtl = (
  bucketWidth: OpenAiUsageBucketWidth,
  segmentEndTimeSec: number,
  fetchedAt = Date.now()
): number | null => {
  const width = bucketWidth === '1h' ? 3600 : DAY_SECONDS;
  return segmentEndTimeSec % width === 0 && segmentEndTimeSec <= finalizationBoundary(bucketWidth, fetchedAt)
    ? OPENAI_SEGMENT_CACHE_TTL_HISTORICAL_MS
    : OPENAI_SEGMENT_CACHE_TTL_RECENT_MS;
};

/**
 * Costs are daily-only. Normalize full-day and current-day requests to UTC day
 * boundaries, but skip rolling/partial historical hourly ranges because a
 * daily result would overstate those views.
 */
export const resolveOpenAiCostReportRange = (
  bucketWidth: OpenAiUsageBucketWidth,
  startTime: number,
  endTime: number,
  nowTime: number = Math.floor(Date.now() / 1000)
): TimeRange | null => {
  if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime) return null;

  const normalizedStart = Math.floor(startTime / DAY_SECONDS) * DAY_SECONDS;
  const normalizedEnd = Math.ceil(endTime / DAY_SECONDS) * DAY_SECONDS;
  const startsAtUtcMidnight = startTime === normalizedStart;
  const coversAtLeastOneCompleteDay = endTime - startTime >= DAY_SECONDS - 1;
  const todayStart = Math.floor(nowTime / DAY_SECONDS) * DAY_SECONDS;
  const coversCurrentDayToNow = normalizedStart === todayStart
    && endTime >= nowTime - COST_RANGE_NOW_TOLERANCE_SECONDS;

  if (
    bucketWidth === '1h'
    && (!startsAtUtcMidnight || (!coversAtLeastOneCompleteDay && !coversCurrentDayToNow))
  ) {
    return null;
  }

  return {
    start: normalizedStart,
    end: Math.max(normalizedStart + DAY_SECONDS, normalizedEnd),
  };
};

export const splitTimeRangesAtBoundary = (ranges: TimeRange[], boundarySec: number): TimeRange[] => {
  const result: TimeRange[] = [];
  for (const range of ranges) {
    if (boundarySec <= range.start || boundarySec >= range.end) {
      result.push(range);
      continue;
    }
    result.push({ start: range.start, end: boundarySec });
    result.push({ start: boundarySec, end: range.end });
  }
  return result.filter(range => range.end > range.start);
};

const sortBucketsByStartTime = (a: OpenAiUsageBucket, b: OpenAiUsageBucket): number => {
  const aStart = Number(a?.start_time ?? a?.startTime ?? 0);
  const bStart = Number(b?.start_time ?? b?.startTime ?? 0);
  return aStart - bStart;
};

const filterBucketsByRange = (buckets: OpenAiUsageBucket[], startTime: number, endTime: number): OpenAiUsageBucket[] => (
  buckets.filter(bucket => {
    const bucketStart = Number(bucket?.start_time ?? bucket?.startTime);
    return Number.isFinite(bucketStart) && bucketStart >= startTime && bucketStart < endTime;
  })
);

const normalizeOpenAiSegmentToRange = (
  segment: OpenAiUsageSegmentPayload,
  startTime: number,
  endTime: number
): OpenAiUsageSegmentPayload => ({
  ...segment,
  startTime,
  endTime,
  completions: filterBucketsByRange(segment.completions ?? [], startTime, endTime),
  embeddings: filterBucketsByRange(segment.embeddings ?? [], startTime, endTime),
  moderations: filterBucketsByRange(segment.moderations ?? [], startTime, endTime),
  images: filterBucketsByRange(segment.images ?? [], startTime, endTime),
  audioSpeeches: filterBucketsByRange(segment.audioSpeeches ?? [], startTime, endTime),
  audioTranscriptions: filterBucketsByRange(segment.audioTranscriptions ?? [], startTime, endTime),
  costs: filterBucketsByRange(segment.costs ?? [], startTime, endTime)
});

/** Only complete buckets fetched after the reporting grace period are retained.
 * In particular, the clock advancing cannot finalize an old partial snapshot. */
const classifySegment = (segment: OpenAiUsageSegmentPayload): OpenAiUsageSegmentPayload[] => {
  const width = segment.bucketWidth === '1h' ? 3600 : DAY_SECONDS;
  const boundary = Math.min(Math.floor(segment.endTime / width) * width,
    finalizationBoundary(segment.bucketWidth, segment.fetchedAt));
  return splitTimeRangesAtBoundary([{ start: segment.startTime, end: segment.endTime }], boundary)
    .map(range => {
      const finalized = range.end <= boundary && range.start % width === 0;
      return { ...normalizeOpenAiSegmentToRange(segment, range.start, range.end), finalized,
        freshUntil: finalized ? RETAINED_FRESH_UNTIL : segment.fetchedAt + LIVE_RESOURCE_TTL_MS };
    });
};

const storeSegment = (segment: OpenAiUsageSegmentPayload, revision: string) =>
  openaiCache.cacheUsageSegment(segment.bucketWidth, segment.startTime, segment.endTime, segment,
    segment.finalized ? null : Math.max(1, segment.freshUntil! - Date.now()), revision);

export const collectOpenAiBuckets = (
  segments: OpenAiUsageSegmentPayload[],
  key: keyof Pick<OpenAiUsageSegmentPayload, 'completions' | 'embeddings' | 'moderations' | 'images' | 'audioSpeeches' | 'audioTranscriptions' | 'costs'>,
  startTime: number,
  endTime: number
): OpenAiUsageBucket[] => {
  const bucketMap = new Map<number, OpenAiUsageBucket>();

  for (const segment of [...segments].sort((a, b) => a.fetchedAt - b.fetchedAt)) {
    const buckets = segment[key] ?? [];
    for (const bucket of buckets) {
      const bucketStart = Number(bucket?.start_time ?? bucket?.startTime);
      if (!Number.isFinite(bucketStart)) continue;
      if (bucketStart < startTime || bucketStart >= endTime) continue;
      bucketMap.set(bucketStart, bucket);
    }
  }

  return Array.from(bucketMap.values()).sort(sortBucketsByStartTime);
};

export const fetchOpenAiUsageSegment = async (
  apiKey: string,
  bucketWidth: OpenAiUsageBucketWidth,
  startTime: number,
  endTime: number
): Promise<{ payload: OpenAiUsageSegmentPayload; errors: string[] }> => {
  const fetchedAt = Date.now();
  const [completionsRes, embeddingsRes, moderationsRes, imagesRes, audioSpeechesRes, audioTranscriptionsRes] =
    await Promise.all([
      getCompletionsUsage(apiKey, startTime, endTime, bucketWidth),
      getEmbeddingsUsage(apiKey, startTime, endTime, bucketWidth),
      getModeratioUsage(apiKey, startTime, endTime, bucketWidth),
      getImagesUsage(apiKey, startTime, endTime, bucketWidth),
      getAudioSpeechesUsage(apiKey, startTime, endTime, bucketWidth),
      getAudioTranscriptionsUsage(apiKey, startTime, endTime, bucketWidth)
    ]);

  // Surface (rather than silently swallow) upstream usage failures. OpenAI
  // currently returns HTTP 500 for every bucket in the in-progress UTC day;
  // previously these `success:false` responses were coerced to `[]` with no
  // signal, so the UI showed a misleading $0. Callers turn these into
  // partialErrors and skip caching the failed segment so it re-fetches on
  // recovery. Mirrors the Anthropic policy: don't hide non-429 fetch failures.
  const usageResponses: Array<[string, ApiResponse<OpenAiUsagePage>]> = [
    ['completions', completionsRes],
    ['embeddings', embeddingsRes],
    ['moderations', moderationsRes],
    ['images', imagesRes],
    ['audio_speeches', audioSpeechesRes],
    ['audio_transcriptions', audioTranscriptionsRes]
  ];
  const errors: string[] = [];
  const failedUsage = usageResponses.filter(([, res]) => !res.success);
  if (failedUsage.length > 0) {
    const detail = failedUsage.find(([, res]) => res.error)?.[1].error ?? 'server error';
    errors.push(`usage endpoints failed (${failedUsage.map(([name]) => name).join(', ')}): ${detail}`);
  }

  const payload: OpenAiUsageSegmentPayload = {
    version: OPENAI_USAGE_SEGMENT_CACHE_VERSION,
    bucketWidth,
    startTime,
    endTime,
    fetchedAt,
    completions: completionsRes.success && Array.isArray(completionsRes.data?.data) ? completionsRes.data.data : [],
    embeddings: embeddingsRes.success && Array.isArray(embeddingsRes.data?.data) ? embeddingsRes.data.data : [],
    moderations: moderationsRes.success && Array.isArray(moderationsRes.data?.data) ? moderationsRes.data.data : [],
    images: imagesRes.success && Array.isArray(imagesRes.data?.data) ? imagesRes.data.data : [],
    audioSpeeches: audioSpeechesRes.success && Array.isArray(audioSpeechesRes.data?.data) ? audioSpeechesRes.data.data : [],
    audioTranscriptions: audioTranscriptionsRes.success && Array.isArray(audioTranscriptionsRes.data?.data) ? audioTranscriptionsRes.data.data : [],
    // Costs are fetched once for the complete requested range below. Keeping
    // them out of raw usage segments avoids repeated daily-cost calls when a
    // long range is split into several provider-limited usage requests.
    costs: []
  };

  return { payload, errors };
};

export const fetchOpenAiUsageWithSegmentCache = async (
  apiKey: string,
  bucketWidth: OpenAiUsageBucketWidth,
  startTime: number,
  endTime: number,
  onProgress?: (message: string) => void,
  credentialRevision: string = readAdminCredentialRevision(),
  options: { forceRefresh?: boolean } = {}
): Promise<OpenAiSegmentFetchResult> => withProviderResourceLock(
  `openai:usage:${credentialRevision}:${bucketWidth}`,
  () => fetchCachedUsage(apiKey, bucketWidth, startTime, endTime, onProgress, credentialRevision, options),
  options.forceRefresh,
  `${startTime}:${endTime}`
);

const fetchCachedUsage = async (
  apiKey: string,
  bucketWidth: OpenAiUsageBucketWidth,
  startTime: number,
  requestedEndTime: number,
  onProgress: ((message: string) => void) | undefined,
  credentialRevision: string,
  options: { forceRefresh?: boolean }
): Promise<OpenAiSegmentFetchResult> => {
  let endTime = requestedEndTime;
  const cachedRaw = await openaiCache.getUsageSegments(
    bucketWidth,
    startTime,
    endTime,
    credentialRevision
  );
  const cachedSegments: OpenAiUsageSegmentPayload[] = cachedRaw
    .map(segment => segment.data as OpenAiUsageSegmentPayload)
    .filter(segment => (
      segment?.version === OPENAI_USAGE_SEGMENT_CACHE_VERSION &&
      segment.bucketWidth === bucketWidth &&
      Number.isFinite(segment.startTime) &&
      Number.isFinite(segment.endTime) &&
      Number.isFinite(segment.fetchedAt) &&
      segment.endTime > startTime &&
      segment.startTime < endTime
    ));
  const usableCachedSegments: OpenAiUsageSegmentPayload[] = [];
  for (const segment of cachedSegments) {
    const parts = classifySegment(segment);
    if (options.forceRefresh && parts.some(part => !part.finalized)) {
      await openaiCache.removeUsageSegment(bucketWidth, segment.startTime, segment.endTime, credentialRevision);
    }
    for (const part of parts) {
      if (!part.finalized && (options.forceRefresh || part.freshUntil! <= Date.now())) continue;
      usableCachedSegments.push(part);
      // Persist a newly split finalized prefix; never renew mutable TTLs.
      if (part.finalized && !segment.finalized) await storeSegment(part, credentialRevision);
    }
  }

  // Freshness is a data watermark, not a fictitious extension of coverage to
  // wall-clock now. Reload/monitor callers with slightly different ends reuse it.
  const now = Date.now();
  const latestHead = usableCachedSegments
    .filter(segment => segment.fetchedAt + LIVE_RESOURCE_TTL_MS > now && segment.endTime <= endTime)
    .sort((a, b) => b.endTime - a.endTime)[0];
  if (!options.forceRefresh && Math.abs(endTime * 1000 - now) < 5 * 60 * 1000
    && latestHead && latestHead.endTime > startTime && now - latestHead.endTime * 1000 < LIVE_RESOURCE_TTL_MS) {
    endTime = latestHead.endTime;
  }

  const targetRange: TimeRange = { start: startTime, end: endTime };
  const coveredRanges = usableCachedSegments.map(segment => ({ start: segment.startTime, end: segment.endTime }));
  const missingRanges = subtractTimeRanges(targetRange, coveredRanges);

  const bucketSeconds = bucketWidth === '1h' ? 60 * 60 : 24 * 60 * 60;
  const maxBucketsPerRequest = bucketWidth === '1h' ? 168 : 31;
  const maxSpanSeconds = bucketSeconds * maxBucketsPerRequest;
  // Re-fetch the complete bucket containing a missing tail. A bucket response
  // replaces the older bucket; adding partial snapshots would lose/double count.
  const alignedMissingRanges = mergeTimeRanges(missingRanges.map(range => ({
    start: Math.floor(range.start / bucketSeconds) * bucketSeconds, end: range.end
  })));
  const requestRanges = splitTimeRangesByMaxSpan(alignedMissingRanges, maxSpanSeconds);

  const fetchedSegments: OpenAiUsageSegmentPayload[] = [];
  const successfulSegments: OpenAiUsageSegmentPayload[] = [];
  const partialErrors: string[] = [];

  for (let index = 0; index < requestRanges.length; index += 1) {
    const range = requestRanges[index];
    onProgress?.(`Fetching OpenAI usage segments (${index + 1}/${requestRanges.length})...`);

    try {
      const { payload: segment, errors } = await fetchOpenAiUsageSegment(apiKey, bucketWidth, range.start, range.end);

      if (errors.length > 0) {
        fetchedSegments.push(segment);
        // Upstream returned errors for this window (e.g. OpenAI 500s for the
        // in-progress UTC day). Surface it to the UI and DON'T cache the failed
        // segment, so a later reload re-fetches once OpenAI recovers.
        const rangeStart = new Date(range.start * 1000).toISOString();
        const rangeEnd = new Date(range.end * 1000).toISOString();
        partialErrors.push(`Segment ${rangeStart} to ${rangeEnd}: ${errors.join('; ')}`);
      } else {
        for (const part of classifySegment(segment)) {
          fetchedSegments.push(part);
          successfulSegments.push(part);
          await storeSegment(part, credentialRevision);
        }
      }
    } catch (error) {
      const rangeStart = new Date(range.start * 1000).toISOString();
      const rangeEnd = new Date(range.end * 1000).toISOString();
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.warn(`[OpenAI Usage] Failed to fetch segment ${rangeStart} - ${rangeEnd}: ${errorMsg}`);
      partialErrors.push(`Segment ${rangeStart} to ${rangeEnd}: ${errorMsg}`);
      // Continue with remaining segments — don't throw
    }
  }

  const allSegments = [...usableCachedSegments, ...fetchedSegments];
  const completionBuckets = collectOpenAiBuckets(allSegments, 'completions', startTime, endTime);
  const embeddingBuckets = collectOpenAiBuckets(allSegments, 'embeddings', startTime, endTime);
  const moderationBuckets = collectOpenAiBuckets(allSegments, 'moderations', startTime, endTime);
  const imageBuckets = collectOpenAiBuckets(allSegments, 'images', startTime, endTime);
  const audioSpeechBuckets = collectOpenAiBuckets(allSegments, 'audioSpeeches', startTime, endTime);
  const audioTranscriptionBuckets = collectOpenAiBuckets(allSegments, 'audioTranscriptions', startTime, endTime);
  let costsUsage: ApiResponse<OpenAiUsagePage> | null = null;
  let freshUntil = Math.min(RETAINED_FRESH_UNTIL, ...allSegments.map(segment =>
    segment.freshUntil ?? segment.fetchedAt + LIVE_RESOURCE_TTL_MS));
  let fetchedAt = allSegments.length ? Math.max(...allSegments.map(segment => segment.fetchedAt)) : Date.now();
  const costReportRange = resolveOpenAiCostReportRange(bucketWidth, startTime, requestedEndTime);
  if (costReportRange) {
    try {
      const costSnapshot = await getCachedOpenAiCosts(
        apiKey,
        costReportRange.start,
        costReportRange.end,
        credentialRevision,
        options.forceRefresh
      );
      const costResponse = costSnapshot.value;
      freshUntil = Math.min(freshUntil, costSnapshot.freshUntil);
      fetchedAt = Math.min(fetchedAt, costSnapshot.fetchedAt);
      costsUsage = costResponse;
      if (!costResponse.success) {
        const error = costResponse.error ?? 'server error';
        console.warn('[OpenAI Usage] Failed to fetch cost report:', error);
        partialErrors.push(`Cost report: ${error}`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.warn('[OpenAI Usage] Failed to fetch cost report:', errorMessage);
      partialErrors.push(`Cost report: ${errorMessage}`);
      costsUsage = { success: false, error: errorMessage };
    }
  }

  const finalCovered = mergeTimeRanges([...usableCachedSegments, ...successfulSegments]
    .map(segment => ({ start: segment.startTime, end: segment.endTime })));
  const uncovered = subtractTimeRanges(targetRange, finalCovered);

  return {
    dataEndTime: endTime,
    fetchedAt,
    freshUntil,
    completionsUsage: { success: true, data: { data: completionBuckets } },
    embeddingsUsage: { success: true, data: { data: embeddingBuckets } },
    moderationsUsage: { success: true, data: { data: moderationBuckets } },
    imagesUsage: { success: true, data: { data: imageBuckets } },
    audioSpeechesUsage: { success: true, data: { data: audioSpeechBuckets } },
    audioTranscriptionsUsage: { success: true, data: { data: audioTranscriptionBuckets } },
    costsUsage,
    coverageComplete: uncovered.length === 0,
    reusedSegments: usableCachedSegments.length,
    fetchedSegments: requestRanges.length,
    partialErrors
  };
};
